import { Component, ComponentInterface, Element, Event, EventEmitter, h, Host, Method, Prop, State } from "@stencil/core";
import { ChevronDown, ChevronUp, ErrorFlag, InformationFlag, WarningFlag } from "../icons";
import { Message } from "../message-list/interface";
import { ErrorButton, WorkflowState } from "./error-workflow-button";

@Component({
    tag: "uic-message-list-item",
    styleUrl: "message-list-item.scss"
})
export class MessageListItem implements ComponentInterface {
    @Element() host: HTMLUicMessageListItemElement;

    /**
     * The message that is shown via this component
     */
    // eslint-disable-next-line
    @Prop() message: Message;

    /**
     * Message-ID, used to expand the children of a specific parent message
     */
    @Prop({ reflect: true }) readonly mid: string;

    /**
     * userLocale is the language selected by user'
     */
    @Prop() readonly userLocale: string;

    /**
     * Translated text for 'show additional information' for the max view central column
     */
    @Prop() readonly additionalInfoText: string;

    /**
     * Translated text for 'error taken by'
     */
    @Prop() readonly inProgressText: string = "In Progress by";

    /**
     * Translated text for 'acknowledged by'
     */
    @Prop() readonly acknowledgedByText: string = "Acknowledged by";

    /**
     * Show small side bar or large full screen menu
     */
    @Prop() readonly variant: "min" | "max" = "min";

    /**
     * List of child messages that can be collapsed/expanded in max view
     */
    @Prop() readonly childrenMessages: Message[];

    /**
     * Current user's id
     */
    @Prop() readonly userId: string = "userId";

    /**
     When hiding a message and show in History this event will be emitted.
     */
    @Event({ eventName: "uicHideClicked" }) hideClicked: EventEmitter<Message>;

    /**
     *  When showing a message in Current, this event will be emitted.
     */
    @Event({ eventName: "uicShowClicked" }) showClicked: EventEmitter<Message>;

    /**
     * When clicking a message, the message is read and this event will be emitted.
     */
    @Event({ eventName: "uicMessageClicked" })
    messageClicked: EventEmitter<Message>;

    /**
     * When the status in the workflow changes, this event will be emitted.
     */
    @Event({ eventName: "uicWorkflowChanged" })
    workflowChanged: EventEmitter<Message>;

    @State() childrenExpanded = false;

    private dateFormatDay: Intl.DateTimeFormatOptions = {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric"
    };
    private dateFormatTime: Intl.DateTimeFormatOptions = {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit"
    };

    private onHideClick(event: MouseEvent) {
        event.cancelBubble = true;
        this.message.visible = false;
        this.message.read = true;
        this.hideClicked.emit(this.message);
    }

    private onShowClick(event: MouseEvent) {
        event.cancelBubble = true;
        this.message.visible = true;
        this.message.read = true;
        this.showClicked.emit(this.message);
    }

    private onMessageClick(m: Message) {
        if (m.visible || m.visible === undefined) {
            m.read = !m.read;
            this.messageClicked.emit(m);
            this.message = Object.assign({}, this.message);
        }
    }

    @Method()
    async expandCollapseChildren(expand: boolean, event) {
        this.childrenExpanded = expand;
        if (event) {
            event.stopPropagation();
        }
    }

    private drawIcon(type: string) {
        switch (type) {
            case "Error":
                return [ErrorFlag()];
            case "Warning":
                return [WarningFlag()];
            case "Information":
                return [InformationFlag()];
        }
    }

    private visibleIcon(visible: boolean) {
        switch (visible) {
            case false:
                return "uic:eye-show";
            default:
                return "uic:eye-hide";
        }
    }

    private showHideClick(event: MouseEvent) {
        switch (this.message.visible) {
            case false:
                return this.onShowClick(event);
            default:
                return this.onHideClick(event);
        }
    }

    private changeWorkflowStatus(event: MouseEvent, newState: WorkflowState) {
        event.stopPropagation();
        if (newState === WorkflowState.Open) {
            this.message.status = newState;
            this.message.commitedBy = "";
            this.message.commitedAt = null;
        } else if (newState === WorkflowState.Committed) {
            this.message.status = newState;
            this.message.commitedAt = new Date();
            this.message.commitedBy = this.userId;
        } else {
            this.message.status = newState;
            this.message.closedAt = new Date();
            this.message.closedBy = this.userId;
        }
        this.workflowChanged.emit(this.message);
        // eslint-disable-next-line
        this.message = Object.assign({}, this.message);
    }

    private openUrl(event, url: string) {
        if (url) {
            window.open(url);
        }
        event.stopPropagation();
    }

    private renderChildrenMessages() {
        if (this.variant === "max" && this.childrenMessages.length > 0 && this.childrenExpanded) {
            return (
                <div class='uic-message-list-item__sub-message-container'>
                    {this.childrenMessages.map((m, index) => (
                        <div
                            class={`uic-message-list-item__message-container-max uic-message-list-item__message-container-max--sub
                                ${
                                    index === this.childrenMessages.length - 1
                                        ? "uic-message-list-item__message-container-max--sub-last"
                                        : ""
                                }`}
                        >
                            <div class={`uic-message-list-item__id`}>{m.messageId}</div>
                            <div
                                class={`uic-message-list-item__title-max ${
                                    m.type === "Error" ? "uic-message-list-item__title--error" : ""
                                }`}
                            >
                                {m.messageTitle}
                            </div>
                            <div class='uic-message-list-item__date uic-message-list-item__date--sub'>
                                {new Date(m.creationDate).toLocaleDateString(this.userLocale, this.dateFormatDay)}
                            </div>
                            <div class='uic-message-list-item__time uic-message-list-item__time--sub'>
                                {new Date(m.creationDate).toLocaleTimeString(this.userLocale, this.dateFormatTime)}
                            </div>
                            <div class={`uic-message-list-item__message-text-max`}>{m.messageText}</div>
                            <div class='uic-message-list-item__message-text-center-max'>{m.additionalInfo}</div>
                            {m.additionalInfoUrl ? (
                                <div
                                    class='uic-message-list-item__message-text-center-link'
                                    onClick={(event) => this.openUrl(event, m.additionalInfoUrl)}
                                >
                                    {this.additionalInfoText}
                                </div>
                            ) : (
                                <div></div>
                            )}
                        </div>
                    ))}
                </div>
            );
        } else {
            return;
        }
    }

    render() {
        return (
            <Host>
                <div>
                    <div
                        class={`uic-message-list-item__message-container-${this.variant} ${
                            !this.message.read ? "uic-message-list-item__new-message" : ""
                        } ${!this.message.visible ? "uic-message-list-item__message-container-max-history" : ""}`}
                        onClick={() => this.onMessageClick(this.message)}
                    >
                        <div class={`uic-message-list-item__icon-${this.variant}`}>
                            <span class={`uic-message-list-item__icon__wrapper__icon`}>{this.drawIcon(this.message.type)}</span>
                        </div>
                        <div
                            class={`uic-message-list-item__id ${
                                !this.message.read ? "uic-message-list-item__new-message--extrabold" : ""
                            }`}
                        >
                            {this.message.messageId}
                        </div>
                        <div
                            onClick={(event) => this.openUrl(event, this.message.sourceApplicationUrl)}
                            class={`uic-message-list-item__app ${
                                !this.message.read ? "uic-message-list-item__new-message--extrabold" : ""
                            }`}
                        >
                            {this.message.sourceApplication}
                        </div>
                        <div
                            class={`uic-message-list-item__title-${this.variant} ${
                                this.message.type === "Error" ? "uic-message-list-item__title--error" : ""
                            } ${!this.message.read ? "uic-message-list-item__new-message--extrabold" : ""}`}
                        >
                            {this.message.messageTitle}
                        </div>
                        {this.variant === "max" ? (
                            <div class='uic-message-list-item__date'>
                                {new Date(this.message.creationDate).toLocaleDateString(this.userLocale, this.dateFormatDay)}
                            </div>
                        ) : (
                            <div></div>
                        )}
                        {this.variant === "max" ? (
                            <div class='uic-message-list-item__time'>
                                {new Date(this.message.creationDate).toLocaleTimeString(this.userLocale, this.dateFormatTime)}
                            </div>
                        ) : (
                            <div></div>
                        )}
                        {this.variant === "max" && this.childrenMessages.length > 0 ? (
                            <div
                                class='uic-message-list-item__expand-arrow'
                                onClick={(event) => this.expandCollapseChildren(!this.childrenExpanded, event)}
                            >
                                {this.childrenExpanded ? ChevronUp() : ChevronDown()}
                            </div>
                        ) : (
                            <div></div>
                        )}
                        <div class={`uic-message-list-item__message-text-${this.variant}`}>{this.message.messageText}</div>
                        {this.variant === "max" ? (
                            <div class='uic-message-list-item__message-text-center-max'>{this.message.additionalInfo}</div>
                        ) : (
                            <div></div>
                        )}
                        {this.message.additionalInfoUrl && this.variant === "max" ? (
                            <div
                                class='uic-message-list-item__message-text-center-link'
                                onClick={(event) => this.openUrl(event, this.message.additionalInfoUrl)}
                            >
                                {this.additionalInfoText}
                            </div>
                        ) : (
                            <div></div>
                        )}
                        {this.message.type !== "Error" ? (
                            <uic-button
                                size='icon'
                                onClick={(event) => {
                                    this.showHideClick(event);
                                }}
                                class={`uic-message-list-item__message-button`}
                                icon={this.visibleIcon(this.message.visible)}
                            ></uic-button>
                        ) : (
                            <ErrorButton
                                message={this.message}
                                changeWorkflowStatus={(event, state) => this.changeWorkflowStatus(event, state)}
                                userId={this.userId}
                                variant={this.variant}
                                inProgressText={this.inProgressText}
                                acknowledgedByText={this.acknowledgedByText}
                            ></ErrorButton>
                        )}
                    </div>
                    {this.renderChildrenMessages()}
                </div>
            </Host>
        );
    }
}
